/**
 * Fine-grained, per-org capabilities — the unit app access is granted in. A
 * permission is held by a PERSON at an org, independent of any role: roles are
 * titles, permissions are access. Every gated endpoint requires one of these.
 * Platform admins bypass them entirely.
 */
export const PERMISSIONS = [
  { key: 'members.manage', label: 'Members', group: 'Member data' },
  { key: 'messages.send', label: 'Send messages', group: 'Messaging' },
  {
    key: 'messages.manage',
    label: 'Manage scheduled messages',
    group: 'Messaging',
  },
  { key: 'billing.manage', label: 'Billing', group: 'Money' },
  { key: 'rules.edit', label: 'Edit schedule rules', group: 'Luach' },
  { key: 'documents.view', label: 'View documents', group: 'Documents' },
  { key: 'documents.edit', label: 'Edit documents', group: 'Documents' },
  { key: 'settings.edit', label: 'Edit shul settings', group: 'Congregation' },
  { key: 'staff.manage', label: 'Manage staff & roles', group: 'Congregation' },
] as const

export type Permission = (typeof PERMISSIONS)[number]['key']

export const PERMISSION_KEYS: Permission[] = PERMISSIONS.map((p) => p.key)

/**
 * What holding a permission carries with it. Billing is worked against the
 * roster — whose gift it was, which billing account owes — so billing access
 * includes member access rather than being granted beside it. Editing an org's
 * documents starts from reading them, so edit carries view.
 */
export const IMPLIED: Partial<Record<Permission, Permission[]>> = {
  'billing.manage': ['members.manage'],
  'documents.edit': ['documents.view'],
}

/** The permissions a set of grants confers, the implied ones included once. */
export function withImplied<T extends { spaceId: string; permission: string }>(
  grants: T[],
): { spaceId: string; permission: string }[] {
  const out = new Map<string, { spaceId: string; permission: string }>()
  const add = (spaceId: string, permission: string) =>
    out.set(`${spaceId}:${permission}`, { spaceId, permission })
  for (const grant of grants) {
    add(grant.spaceId, grant.permission)
    for (const implied of IMPLIED[grant.permission as Permission] ?? [])
      add(grant.spaceId, implied)
  }
  return [...out.values()]
}

/** The held permission that brings `permission` along, if one does. */
export function impliedBy(
  held: readonly string[],
  permission: Permission,
): Permission | null {
  return (
    (Object.keys(IMPLIED) as Permission[]).find(
      (key) => held.includes(key) && IMPLIED[key]?.includes(permission),
    ) ?? null
  )
}
